import supabase from './supabaseClient'

class SupabaseCommentService {
    /**
     * 获取指定文章的评论列表
     * @param {number} articleId - 文章ID
     * @param {number} page - 页码，从1开始
     * @param {number} pageSize - 每页条数
     */
    async getCommentsByArticleId(articleId, page = 1, pageSize = 20) {
        try {
            const start = (page - 1) * pageSize
            const end = start + pageSize - 1

            // 首先获取评论数据
            const { data: commentsData, error: commentsError, count } = await supabase
                .from('comments')
                .select('*', { count: 'exact' })
                .eq('article_id', articleId)
                .order('created_at', { ascending: false })
                .range(start, end)

            if (commentsError) {
                console.error('获取评论失败:', commentsError)
                throw commentsError
            }

            // 然后获取每个评论作者的profile信息
            const commentsWithProfiles = await Promise.all(
                (commentsData || []).map(async (comment) => {
                    try {
                        const { data: profile } = await supabase
                            .from('profiles')
                            .select('username, display_name, avatar_url')
                            .eq('user_id', comment.user_id)
                            .single()

                        return {
                            ...comment,
                            profiles: profile || null
                        }
                    } catch (profileError) {
                        console.log(`获取用户${comment.user_id}的profile失败:`, profileError)
                        return {
                            ...comment,
                            profiles: null
                        }
                    }
                })
            )

            return {
                comments: commentsWithProfiles || [],
                total: count || 0,
                page,
                pageSize,
                totalPages: Math.ceil((count || 0) / pageSize)
            }
        } catch (error) {
            console.error('获取评论失败:', error)
            throw error
        }
    }

    /**
     * 发表评论
     * @param {number} articleId - 文章ID
     * @param {string} content - 评论内容
     */
    async createComment(articleId, content) {
        try {
            // 检查内容长度
            if (!content || content.trim().length === 0) {
                throw new Error('评论内容不能为空')
            }

            if (content.length > 100) {
                throw new Error('评论内容不能超过100字符')
            }

            // 获取当前用户
            const { data: { user }, error: userError } = await supabase.auth.getUser()
            if (userError || !user) {
                throw new Error('请先登录')
            }

            const { data, error } = await supabase
                .from('comments')
                .insert([
                    {
                        article_id: articleId,
                        user_id: user.id,
                        content: content.trim()
                    }
                ])
                .select('*')
                .single()

            if (error) {
                console.error('发表评论失败:', error)
                throw error
            }

            // 获取用户profile信息
            let profile = null
            try {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('username, display_name, avatar_url')
                    .eq('user_id', user.id)
                    .single()
                profile = profileData
            } catch (profileError) {
                console.log('获取用户profile失败:', profileError)
            }

            // 返回包含profile信息的评论数据
            return {
                ...data,
                profiles: profile
            }
        } catch (error) {
            console.error('发表评论失败:', error)
            throw error
        }
    }

    /**
     * 删除评论（只能删除自己的评论或管理员可以删除任何评论）
     * @param {number} commentId - 评论ID
     */
    async deleteComment(commentId) {
        try {
            // 获取当前用户
            const { data: { user }, error: userError } = await supabase.auth.getUser()
            if (userError || !user) {
                throw new Error('请先登录')
            }

            // 检查用户权限：只能删除自己的评论，或者管理员可以删除任何评论
            const { data: profile } = await supabase
                .from('profiles')
                .select('role')
                .eq('user_id', user.id)
                .single()

            const isAdmin = profile?.role === 'admin'

            let query = supabase
                .from('comments')
                .delete()
                .eq('id', commentId)

            // 如果不是管理员，只能删除自己的评论
            if (!isAdmin) {
                query = query.eq('user_id', user.id)
            }

            const { error } = await query

            if (error) {
                console.error('删除评论失败:', error)
                throw error
            }

            return true
        } catch (error) {
            console.error('删除评论失败:', error)
            throw error
        }
    }

    /**
     * 获取评论总数
     * @param {number} articleId - 文章ID
     */
    async getCommentCount(articleId) {
        try {
            const { count, error } = await supabase
                .from('comments')
                .select('*', { count: 'exact', head: true })
                .eq('article_id', articleId)

            if (error) {
                console.error('获取评论数量失败:', error)
                throw error
            }

            return count || 0
        } catch (error) {
            console.error('获取评论数量失败:', error)
            throw error
        }
    }
}

const supabaseCommentService = new SupabaseCommentService()
export default supabaseCommentService 